import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { PlatformReplies } from "@/components/mod/ModForwarding";
import { Search, ExternalLink, Archive, ArchiveRestore, Trash2, Loader2, FileDown, BookOpen, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { SOURCE_LABELS } from "@/i18n";
import { useStatuses, statusBadgeStyle } from "@/context/StatusContext";
import { SlaBadge } from "@/components/mod/SlaBadge";
import { DecisionChecklist } from "@/components/mod/DecisionChecklist";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const LBL = {
  en: { updates: "Reporter updates", newTag: "new", reporterNote: "Reporter", added: "Added",
    active: "Active", archived: "Archived", archive: "Archive", unarchive: "Restore", del: "Delete",
    delTitle: "Delete report?", delDesc: "This permanently removes the report and any related dispute. This cannot be undone.",
    cancel: "Cancel", archivedOk: "Report archived", restoredOk: "Report restored", deletedOk: "Report deleted", archivedTag: "archived",
    selected: "selected", selectAll: "Select all", clear: "Clear", setStatus: "Set status", bulkDelTitle: "Delete selected reports?", bulkDelDesc: "selected report(s) and any related disputes will be permanently deleted. This cannot be undone.", pdf: "Download PDF", pdfErr: "PDF export failed" },
  hu: { updates: "Bejelentői frissítések", newTag: "új", reporterNote: "Bejelentő", added: "Hozzáadva",
    active: "Aktív", archived: "Archivált", archive: "Archiválás", unarchive: "Visszaállítás", del: "Törlés",
    delTitle: "Törli a bejelentést?", delDesc: "Ez véglegesen eltávolítja a bejelentést és a kapcsolódó vitát. A művelet nem vonható vissza.",
    cancel: "Mégse", archivedOk: "Bejelentés archiválva", restoredOk: "Bejelentés visszaállítva", deletedOk: "Bejelentés törölve", archivedTag: "archivált",
    selected: "kijelölve", selectAll: "Összes kijelölése", clear: "Kijelölés törlése", setStatus: "Státusz beállítása", bulkDelTitle: "Kijelölt bejelentések törlése?", bulkDelDesc: "kijelölt bejelentés és a kapcsolódó viták végleges törlése. A művelet nem vonható vissza.", pdf: "PDF letöltése", pdfErr: "A PDF exportálása sikertelen" },
  de: { updates: "Aktualisierungen des Melders", newTag: "neu", reporterNote: "Melder", added: "Hinzugefügt",
    active: "Aktiv", archived: "Archiviert", archive: "Archivieren", unarchive: "Wiederherstellen", del: "Löschen",
    delTitle: "Meldung löschen?", delDesc: "Dies entfernt die Meldung und den zugehörigen Streitfall dauerhaft. Kann nicht rückgängig gemacht werden.",
    cancel: "Abbrechen", archivedOk: "Meldung archiviert", restoredOk: "Meldung wiederhergestellt", deletedOk: "Meldung gelöscht", archivedTag: "archiviert",
    selected: "ausgewählt", selectAll: "Alle auswählen", clear: "Leeren", setStatus: "Status setzen", bulkDelTitle: "Ausgewählte Meldungen löschen?", bulkDelDesc: "ausgewählte Meldung(en) und zugehörige Streitfälle werden dauerhaft gelöscht. Kann nicht rückgängig gemacht werden.", pdf: "PDF herunterladen", pdfErr: "PDF-Export fehlgeschlagen" },
  fr: { updates: "Mises à jour du signalant", newTag: "nouveau", reporterNote: "Signalant", added: "Ajouté",
    active: "Actif", archived: "Archivé", archive: "Archiver", unarchive: "Restaurer", del: "Supprimer",
    delTitle: "Supprimer le signalement ?", delDesc: "Ceci supprime définitivement le signalement et tout litige associé. Action irréversible.",
    cancel: "Annuler", archivedOk: "Signalement archivé", restoredOk: "Signalement restauré", deletedOk: "Signalement supprimé", archivedTag: "archivé",
    selected: "sélectionné(s)", selectAll: "Tout sélectionner", clear: "Effacer", setStatus: "Définir le statut", bulkDelTitle: "Supprimer les signalements sélectionnés ?", bulkDelDesc: "signalement(s) sélectionné(s) et litiges associés seront définitivement supprimés. Action irréversible.", pdf: "Télécharger le PDF", pdfErr: "Échec de l'export PDF" },
};

export function ModReports() {
  const { lang } = useLang();
  const { statuses, label: stLabel, color: stColor, closes } = useStatuses();
  const [reports, setReports] = useState([]);
  const [cats, setCats] = useState({});
  const [selRef, setSelRef] = useState(null);
  const [q, setQ] = useState("");
  const [sla, setSla] = useState({ default_hours: 24, categories: {} });
  const [showArchived, setShowArchived] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [checked, setChecked] = useState([]);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [guidelines, setGuidelines] = useState([]);
  const [glOpen, setGlOpen] = useState(false);
  const [glQ, setGlQ] = useState("");
  const [glPlatform, setGlPlatform] = useState("all");

  const load = (arch = showArchived) =>
    api.get(`/mod/reports?archived=${arch}`).then((r) => setReports(r.data));
  useEffect(() => {
    load(showArchived);
    setSelRef(null);
    setChecked([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);
  useEffect(() => {
    api.get("/categories").then((r) => { const m = {}; r.data.forEach((c) => (m[c.key] = c)); setCats(m); });
    api.get("/sla").then((r) => setSla(r.data)).catch(() => {});
    api.get("/mod/guidelines").then((r) => setGuidelines(r.data)).catch(() => {});
  }, []);

  const hoursFor = (cat) => sla.categories?.[cat] || sla.default_hours || 24;

  // Live tick so SLA timers stay current.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((v) => v + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const catLabel = (k) => cats[k]?.[lang] || cats[k]?.en || k;
  const selected = reports.find((r) => r.reference === selRef) || null;
  const t = LBL[lang] || LBL.en;

  const openReport = (ref) => {
    setSelRef(ref);
    const r = reports.find((x) => x.reference === ref);
    if (r?.has_new_reporter_update) {
      api.patch(`/mod/reports/${ref}/seen`).catch(() => {});
      setReports((rs) => rs.map((x) => (x.reference === ref ? { ...x, has_new_reporter_update: false } : x)));
    }
  };

  const filtered = useMemo(() => {
    let list = reports;
    if (q.trim()) {
      const s = q.toLowerCase();
      list = reports.filter((r) =>
        [r.reference, r.platform, r.category, r.description].join(" ").toLowerCase().includes(s)
      );
    }
    // Pin reports with new reporter updates to the top; keep newest-first otherwise.
    return [...list].sort((a, b) => (b.has_new_reporter_update ? 1 : 0) - (a.has_new_reporter_update ? 1 : 0));
  }, [reports, q]);

  const doArchive = async (ref, archived) => {
    setBusy(true);
    try {
      await api.patch(`/mod/reports/${ref}/archive`, { archived });
      setReports((rs) => rs.filter((r) => r.reference !== ref));
      setSelRef(null);
      toast.success(archived ? t.archivedOk : t.restoredOk);
    } finally { setBusy(false); }
  };

  const doDelete = async (ref) => {
    setBusy(true);
    try {
      await api.delete(`/mod/reports/${ref}`);
      setReports((rs) => rs.filter((r) => r.reference !== ref));
      setSelRef(null);
      setConfirmDel(null);
      toast.success(t.deletedOk);
    } finally { setBusy(false); }
  };

  const toggleCheck = (ref) =>
    setChecked((c) => (c.includes(ref) ? c.filter((x) => x !== ref) : [...c, ref]));
  const allVisibleChecked = filtered.length > 0 && filtered.every((r) => checked.includes(r.reference));
  const toggleAll = () =>
    setChecked(allVisibleChecked ? [] : filtered.map((r) => r.reference));

  const bulkArchive = async (archived) => {
    setBusy(true);
    try {
      await Promise.all(checked.map((ref) => api.patch(`/mod/reports/${ref}/archive`, { archived })));
      setReports((rs) => rs.filter((r) => !checked.includes(r.reference)));
      if (checked.includes(selRef)) setSelRef(null);
      toast.success(`${archived ? t.archivedOk : t.restoredOk} (${checked.length})`);
      setChecked([]);
    } finally { setBusy(false); }
  };

  const bulkDelete = async () => {
    setBusy(true);
    try {
      await Promise.all(checked.map((ref) => api.delete(`/mod/reports/${ref}`)));
      setReports((rs) => rs.filter((r) => !checked.includes(r.reference)));
      if (checked.includes(selRef)) setSelRef(null);
      toast.success(`${t.deletedOk} (${checked.length})`);
      setChecked([]);
      setConfirmBulk(false);
    } finally { setBusy(false); }
  };

  const downloadPdf = async (ref) => {
    setBusy(true);
    try {
      const res = await api.get(`/mod/reports/${ref}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${ref}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t.pdfErr);
    } finally {
      setBusy(false);
    }
  };

  const toggleGuideline = async (gid) => {
    if (!selected) return;
    const cur = selected.referenced_guidelines || [];
    const next = cur.includes(gid) ? cur.filter((x) => x !== gid) : [...cur, gid];
    setReports((rs) => rs.map((r) => (r.reference === selected.reference ? { ...r, referenced_guidelines: next } : r)));
    try {
      await api.patch(`/mod/reports/${selected.reference}/guidelines`, { ids: next });
    } catch {
      toast.error(t.pdfErr);
      setReports((rs) => rs.map((r) => (r.reference === selected.reference ? { ...r, referenced_guidelines: cur } : r)));
    }
  };

  const glPlatforms = useMemo(
    () => ["all", ...Array.from(new Set(guidelines.map((g) => g.platform)))],
    [guidelines]
  );
  const glFiltered = useMemo(() => {
    let list = guidelines;
    if (glPlatform !== "all") list = list.filter((g) => g.platform === glPlatform);
    if (glQ.trim()) {
      const s = glQ.toLowerCase();
      list = list.filter((g) => [g.platform, g.title, g.content].join(" ").toLowerCase().includes(s));
    }
    return list;
  }, [guidelines, glPlatform, glQ]);

  const changeStatus = async (ref, status) => {    await api.patch(`/mod/reports/${ref}/status`, { status });
    setReports((rs) => rs.map((r) => (r.reference === ref ? { ...r, status } : r)));
    toast.success(`${ref} → ${stLabel(status, lang)}`);
  };

  const bulkStatus = async (status) => {
    setBusy(true);
    try {
      await Promise.all(checked.map((ref) => api.patch(`/mod/reports/${ref}/status`, { status })));
      setReports((rs) => rs.map((r) => (checked.includes(r.reference) ? { ...r, status } : r)));
      toast.success(`${checked.length} → ${stLabel(status, lang)}`);
      setChecked([]);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] min-h-[440px] overflow-hidden rounded-lg border border-zinc-800/70 bg-[#111113]">
      {/* queue */}
      <div className="flex w-full min-w-0 flex-col border-r border-zinc-800/70 lg:w-[360px] lg:shrink-0">
        <div className="border-b border-zinc-800/70 p-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input data-testid="mod-report-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-9 border-zinc-800 bg-zinc-950 pl-9 text-[13px]" />
          </div>
          <div className="mt-2 flex items-center gap-0.5 rounded-md border border-zinc-800 bg-zinc-950 p-0.5">
            <button
              data-testid="mod-filter-active"
              onClick={() => setShowArchived(false)}
              className={`flex-1 rounded px-2 py-1 text-[12px] font-medium transition-colors ${!showArchived ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-200"}`}
            >{t.active}</button>
            <button
              data-testid="mod-filter-archived"
              onClick={() => setShowArchived(true)}
              className={`flex-1 rounded px-2 py-1 text-[12px] font-medium transition-colors ${showArchived ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-200"}`}
            >{t.archived}</button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto" data-testid="mod-reports-body">
          {checked.length > 0 && (
            <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b border-zinc-800/70 bg-zinc-900/95 px-3 py-2 backdrop-blur" data-testid="bulk-action-bar">
              <span className="text-[12px] font-medium text-zinc-200">{checked.length} {t.selected}</span>
              <Select onValueChange={bulkStatus}>
                <SelectTrigger data-testid="bulk-status-select" disabled={busy} className="h-7 w-[150px] border-zinc-700 bg-zinc-800 text-[12px] text-zinc-200">
                  <SelectValue placeholder={t.setStatus} />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  {statuses.map((s) => <SelectItem key={s.key} value={s.key} data-testid={`bulk-status-${s.key}`} className="text-[13px] focus:bg-zinc-800"><span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />{stLabel(s.key, lang)}</span></SelectItem>)}
                </SelectContent>
              </Select>
              <div className="ml-auto flex items-center gap-1.5">
                <Button data-testid="bulk-archive" variant="outline" disabled={busy} onClick={() => bulkArchive(!showArchived)} className="h-7 gap-1 border-zinc-700 bg-zinc-800 px-2 text-[12px] text-zinc-200 hover:bg-zinc-700">
                  {showArchived ? <ArchiveRestore size={13} /> : <Archive size={13} />}{showArchived ? t.unarchive : t.archive}
                </Button>
                <Button data-testid="bulk-delete" variant="outline" disabled={busy} onClick={() => setConfirmBulk(true)} className="h-7 gap-1 border-red-900/60 bg-red-950/30 px-2 text-[12px] text-red-300 hover:bg-red-900/40">
                  <Trash2 size={13} />{t.del}
                </Button>
                <button data-testid="bulk-clear" onClick={() => setChecked([])} className="px-1.5 text-[12px] text-zinc-500 hover:text-zinc-300">{t.clear}</button>
              </div>
            </div>
          )}
          {filtered.length > 0 && (
            <label className="flex cursor-pointer items-center gap-2 border-b border-zinc-800/50 px-4 py-2 text-[11px] text-zinc-500 hover:text-zinc-300">
              <Checkbox data-testid="bulk-select-all" checked={allVisibleChecked} onCheckedChange={toggleAll} className="h-3.5 w-3.5 border-zinc-600" />
              {t.selectAll}
            </label>
          )}
          {filtered.length === 0 && <div className="p-6 text-center text-[13px] text-zinc-600">No reports.</div>}
          {filtered.map((r) => (
            <div
              key={r.reference}
              className={`relative flex items-start gap-2.5 border-b border-zinc-800/50 px-4 py-3 transition-colors ${selRef === r.reference ? "bg-zinc-800/40" : checked.includes(r.reference) ? "bg-blue-950/20" : "hover:bg-zinc-900/60"}`}
            >
              {selRef === r.reference && <span className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-500" />}
              <Checkbox
                data-testid={`mod-report-check-${r.reference}`}
                checked={checked.includes(r.reference)}
                onCheckedChange={() => toggleCheck(r.reference)}
                onClick={(e) => e.stopPropagation()}
                className="mt-0.5 h-4 w-4 shrink-0 border-zinc-600"
              />
              <button
                data-testid={`mod-report-${r.reference}`}
                onClick={() => openReport(r.reference)}
                className="min-w-0 flex-1 text-left"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 font-mono text-[12px] text-zinc-200">
                    {r.has_new_reporter_update && <span data-testid={`new-update-dot-${r.reference}`} className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400" title={t.updates} />}
                    {r.reference}
                  </span>
                  <span className="rounded border px-1.5 py-0.5 text-[10px]" style={statusBadgeStyle(stColor(r.status))}>{stLabel(r.status, lang)}</span>
                </div>
                <div className="mt-1 truncate text-[12px] text-zinc-400">{r.platform} · {catLabel(r.category)}</div>
                <div className="mt-1.5 flex items-center gap-2">
                  <SlaBadge createdAt={r.created_at} status={r.status} lang={lang} hours={hoursFor(r.category)} closed={closes(r.status)} />
                  {r.has_new_reporter_update && <span data-testid={`new-update-tag-${r.reference}`} className="rounded border border-blue-500/40 bg-blue-500/10 px-1.5 py-0.5 text-[10px] text-blue-300">{t.updates} · {t.newTag}</span>}
                  {r.has_new_platform_reply && <span data-testid={`new-platform-reply-tag-${r.reference}`} className="rounded border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-300">Platform válasz · Új</span>}
                  {r.dispute_token && <span className="text-[11px] text-amber-400">⚖ dispute</span>}
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* detail */}
      <div className="hidden min-w-0 flex-1 overflow-y-auto lg:block">
        {selected ? (
          <div className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono text-[15px] font-semibold text-zinc-50">{selected.reference}</div>
                <div className="mt-0.5 text-[13px] text-zinc-400">{selected.platform} · {catLabel(selected.category)}</div>
              </div>
              <div className="flex items-center gap-2">
                <Select value={selected.status} onValueChange={(v) => changeStatus(selected.reference, v)}>
                  <SelectTrigger data-testid={`status-select-${selected.reference}`} className="h-9 w-44 border-zinc-800 bg-zinc-950 text-[13px]"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-900">
                    {statuses.map((s) => <SelectItem key={s.key} value={s.key} className="text-[13px] focus:bg-zinc-800"><span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />{stLabel(s.key, lang)}</span></SelectItem>)}
                  </SelectContent>
                </Select>
                <DecisionChecklist report={selected} onSaved={(a) => setReports((rs) => rs.map((r) => (r.reference === selected.reference ? { ...r, assessment: a } : r)))} />
                <Button data-testid="mod-report-guidelines" variant="outline" disabled={busy} onClick={() => setGlOpen(true)} title="Irányelvek" className="relative h-9 gap-1.5 border-zinc-800 bg-zinc-900 px-2.5 text-zinc-300 hover:bg-zinc-800">
                  <BookOpen size={16} /><span className="hidden text-[13px] xl:inline">Irányelvek</span>
                  {(selected.referenced_guidelines?.length || 0) > 0 && (
                    <span data-testid="guideline-ref-count" className="ml-0.5 rounded-full bg-blue-600 px-1.5 text-[10px] font-semibold text-white">{selected.referenced_guidelines.length}</span>
                  )}
                </Button>
                <Button data-testid="mod-report-pdf" variant="outline" disabled={busy} onClick={() => downloadPdf(selected.reference)} title={t.pdf} className="h-9 gap-1.5 border-zinc-800 bg-zinc-900 px-2.5 text-zinc-300 hover:bg-zinc-800">
                  <FileDown size={16} /><span className="hidden text-[13px] xl:inline">PDF</span>
                </Button>
                {selected.archived ? (
                  <Button data-testid="mod-report-unarchive" variant="outline" disabled={busy} onClick={() => doArchive(selected.reference, false)} title={t.unarchive} className="h-9 border-zinc-800 bg-zinc-900 px-2.5 text-zinc-300 hover:bg-zinc-800">
                    <ArchiveRestore size={16} />
                  </Button>
                ) : (
                  <Button data-testid="mod-report-archive" variant="outline" disabled={busy} onClick={() => doArchive(selected.reference, true)} title={t.archive} className="h-9 border-zinc-800 bg-zinc-900 px-2.5 text-zinc-300 hover:bg-zinc-800">
                    <Archive size={16} />
                  </Button>
                )}
                <Button data-testid="mod-report-delete" variant="outline" disabled={busy} onClick={() => setConfirmDel(selected.reference)} title={t.del} className="h-9 border-red-900/60 bg-red-950/30 px-2.5 text-red-300 hover:bg-red-900/40">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                </Button>
              </div>
            </div>

            <div className="mt-5 space-y-4">
              <Field label="Description"><p className="text-[13px] leading-relaxed text-zinc-300">{selected.description}</p></Field>
              <Field label="Content URL">
                <a href={selected.content_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 break-all font-mono text-[12px] text-blue-400 hover:underline">
                  {selected.content_url} <ExternalLink size={12} />
                </a>
              </Field>
              {selected.attachments?.length > 0 && (
                <Field label="Attachments">
                  <div className="flex flex-wrap gap-1.5">
                    {selected.attachments.map((a) => (
                      <a key={a.id} href={`${BACKEND}${a.url}`} target="_blank" rel="noreferrer" className="rounded border border-zinc-800 bg-zinc-900 px-1.5 py-0.5 font-mono text-[11px] text-zinc-300 hover:bg-zinc-800">{a.filename}</a>
                    ))}
                  </div>
                </Field>
              )}
              <div className="grid grid-cols-2 gap-4">
                <Field label="Reporter"><span className="text-[13px] text-zinc-300">{selected.anonymous ? "— (anonymous)" : `${selected.reporter_name || "—"}`}</span></Field>
                <Field label="Email"><span className="text-[13px] text-zinc-300">{selected.reporter_email || "—"}</span></Field>
                <Field label="Source"><span className="text-[13px] text-zinc-300">{SOURCE_LABELS[lang][selected.source] || selected.source}</span></Field>
                {selected.created_by && <Field label="Created by"><span className="text-[13px] text-zinc-300">{selected.created_by}</span></Field>}
                <Field label="Created"><span className="text-[13px] text-zinc-300">{new Date(selected.created_at).toLocaleString()}</span></Field>
                <Field label="SLA"><SlaBadge createdAt={selected.created_at} status={selected.status} lang={lang} size="lg" hours={hoursFor(selected.category)} closed={closes(selected.status)} /></Field>
                {selected.dispute_token && <Field label="Dispute"><span className="text-[13px] text-amber-400">Out-of-court dispute open</span></Field>}
              </div>

              <Field label="Hivatkozott irányelvek">
                <div className="flex flex-wrap items-center gap-1.5" data-testid="referenced-guidelines">
                  {(selected.referenced_guidelines || []).length === 0 && (
                    <span className="text-[12px] text-zinc-600">Nincs kiválasztva.</span>
                  )}
                  {(selected.referenced_guidelines || []).map((gid) => {
                    const g = guidelines.find((x) => x.id === gid);
                    if (!g) return null;
                    return (
                      <span key={gid} className="inline-flex items-center gap-1.5 rounded-full border border-blue-500/40 bg-blue-500/10 px-2.5 py-1 text-[12px] text-blue-200">
                        <span className="text-blue-400/70">{g.platform}</span> {g.title}
                        <button data-testid={`ref-remove-${gid}`} onClick={() => toggleGuideline(gid)} className="text-blue-300/60 hover:text-blue-100">×</button>
                      </span>
                    );
                  })}
                  <button data-testid="open-guidelines-drawer" onClick={() => setGlOpen(true)} className="inline-flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-900 px-2.5 py-1 text-[12px] text-zinc-300 hover:bg-zinc-800">
                    <BookOpen size={12} /> Kezelés
                  </button>
                </div>
              </Field>

              <Field label="Platform-továbbítás és válaszok">
                <PlatformReplies report={selected} onChanged={() => load()} />
              </Field>

              {selected.reporter_updates?.length > 0 && (
                <Field label={t.updates}>
                  <div className="space-y-2" data-testid="reporter-updates">
                    {[...selected.reporter_updates].reverse().map((u) => (
                      <div key={u.id} className="rounded-lg border border-blue-500/25 bg-blue-500/5 p-3">
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <span className="text-[11px] font-medium text-blue-300">{t.reporterNote}</span>
                          <span className="text-[11px] text-zinc-500">{t.added}: {new Date(u.created_at).toLocaleString()}</span>
                        </div>
                        {u.note && <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-zinc-300">{u.note}</p>}
                        {u.attachments?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {u.attachments.map((a) => (
                              <a key={a.id} href={`${BACKEND}${a.url}`} target="_blank" rel="noreferrer" className="rounded border border-zinc-800 bg-zinc-900 px-1.5 py-0.5 font-mono text-[11px] text-zinc-300 hover:bg-zinc-800">{a.filename}</a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Field>
              )}
            </div>
          </div>
        ) : (
          <div className="grid h-full place-items-center text-[13px] text-zinc-600">Select a report</div>
        )}
      </div>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="border-zinc-800 bg-zinc-900" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-50">{t.delTitle}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              <span className="font-mono text-zinc-300">{confirmDel}</span> — {t.delDesc}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 bg-zinc-800 text-zinc-200 hover:bg-zinc-700">{t.cancel}</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-btn" onClick={() => doDelete(confirmDel)} className="bg-red-600 text-white hover:bg-red-700">{t.del}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmBulk} onOpenChange={setConfirmBulk}>
        <AlertDialogContent className="border-zinc-800 bg-zinc-900" data-testid="bulk-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-50">{t.bulkDelTitle}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              <span className="font-semibold text-zinc-300">{checked.length}</span> {t.bulkDelDesc}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 bg-zinc-800 text-zinc-200 hover:bg-zinc-700">{t.cancel}</AlertDialogCancel>
            <AlertDialogAction data-testid="bulk-delete-confirm-btn" onClick={bulkDelete} className="bg-red-600 text-white hover:bg-red-700">{t.del}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={glOpen} onOpenChange={setGlOpen}>
        <SheetContent side="right" className="flex w-full flex-col border-zinc-800 bg-[#0d0d0f] p-0 text-zinc-100 sm:max-w-md" data-testid="guidelines-drawer">
          <SheetHeader className="border-b border-zinc-800/70 p-4">
            <SheetTitle className="flex items-center gap-2 text-zinc-50">
              <BookOpen size={17} className="text-blue-400" /> Közösségi irányelvek
            </SheetTitle>
            <SheetDescription className="text-left text-[12px] text-zinc-500">
              {selected ? <>Bejelentés: <span className="font-mono text-zinc-400">{selected.reference}</span> · jelöld be, mely irányelvekre hivatkozik a döntés.</> : "Válassz egy bejelentést."}
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-2 border-b border-zinc-800/70 p-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input data-testid="guidelines-search" value={glQ} onChange={(e) => setGlQ(e.target.value)} placeholder="Keresés…" className="h-9 border-zinc-800 bg-zinc-950 pl-9 text-[13px]" />
            </div>
            <div className="flex flex-wrap gap-1">
              {glPlatforms.map((p) => (
                <button
                  key={p}
                  data-testid={`gl-platform-${p}`}
                  onClick={() => setGlPlatform(p)}
                  className={`rounded-full border px-2.5 py-0.5 text-[11px] transition-colors ${glPlatform === p ? "border-blue-500/50 bg-blue-500/15 text-blue-200" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-zinc-200"}`}
                >{p === "all" ? "Összes" : p}</button>
              ))}
            </div>
          </div>

          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
            {glFiltered.length === 0 && <div className="pt-8 text-center text-[13px] text-zinc-600">Nincs találat.</div>}
            {glFiltered.map((g) => {
              const active = (selected?.referenced_guidelines || []).includes(g.id);
              return (
                <button
                  key={g.id}
                  data-testid={`gl-toggle-${g.id}`}
                  onClick={() => toggleGuideline(g.id)}
                  disabled={!selected}
                  className={`block w-full rounded-lg border p-3 text-left transition-colors ${active ? "border-blue-500/50 bg-blue-500/10" : "border-zinc-800 bg-zinc-950 hover:bg-zinc-900"}`}
                >
                  <div className="flex items-start gap-2.5">
                    <span className={`mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border ${active ? "border-blue-500 bg-blue-600 text-white" : "border-zinc-600"}`}>
                      {active && <Check size={11} />}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-zinc-400">{g.platform}</span>
                        <span className="text-[13px] font-medium text-zinc-100">{g.title}</span>
                      </div>
                      {g.content && <p className="mt-1 whitespace-pre-wrap text-[12px] leading-relaxed text-zinc-400">{g.content}</p>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="label-caps mb-1">{label}</div>
      {children}
    </div>
  );
}
